import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  ChartBarIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../utils/supabaseClient';
import ViewToggle from '../components/home/ViewToggle';
import MapView from '../components/home/MapView';

// Color palette for projects
const PROJECT_COLORS = [
  { bg: 'bg-red-500', text: 'text-white' },
  { bg: 'bg-blue-500', text: 'text-white' },
  { bg: 'bg-green-500', text: 'text-white' },
  { bg: 'bg-amber-500', text: 'text-white' },
  { bg: 'bg-purple-500', text: 'text-white' },
  { bg: 'bg-pink-500', text: 'text-white' },
  { bg: 'bg-cyan-500', text: 'text-white' },
  { bg: 'bg-orange-500', text: 'text-white' },
];

export default function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'map' ? 'map' : 'calendar';

  // Track whether the map has been visited at least once. We lazy-mount it
  // on first use but keep it mounted afterwards so toggling back preserves
  // zoom/pan/hover state instead of re-fetching and re-initializing the map.
  const [hasMountedMap, setHasMountedMap] = useState(view === 'map');
  useEffect(() => {
    if (view === 'map' && !hasMountedMap) setHasMountedMap(true);
  }, [view, hasMountedMap]);

  const handleViewChange = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'map') params.set('view', 'map');
    else params.delete('view');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <>
      <div className={view === 'calendar' ? 'contents' : 'hidden'}>
        <CalendarView view={view} onViewChange={handleViewChange} />
      </div>
      {hasMountedMap && (
        <div className={view === 'map' ? 'contents' : 'hidden'}>
          <MapView view={view} onViewChange={handleViewChange} />
        </div>
      )}
    </>
  );
}

export function CalendarView({ view, onViewChange, sessions: suppliedSessions, loading: suppliedLoading, error: suppliedError, routePrefix = '' }) {
  const navigate = useNavigate();
  const [loadedSessions, setAllSessions] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [loadError, setError] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  const allSessions = suppliedSessions ?? loadedSessions;
  const loading = suppliedSessions !== undefined ? suppliedLoading : isLoading;
  const error = suppliedSessions !== undefined ? suppliedError : loadError;
  const monthScrollRef = useRef(null);

  // Load all sessions once
  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: membership } = await supabase
        .from('memberships')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!membership) throw new Error('No company membership found');

      const { data, error: fetchError } = await supabase.rpc('get_company_dashboard_data', {
        p_company_id: membership.company_id
      });

      if (fetchError) throw fetchError;

      const processed = (data || []).map(session => {
        const runDate = session.run_date 
          ? new Date(session.run_date + 'T00:00:00')
          : session.created_at 
            ? new Date(session.created_at) 
            : null;
        
        const startFt = session.start_station_ft || 0;
        const endFt = session.end_station_ft || 0;
        const distanceFt = Math.abs(endFt - startFt);
        
        return {
          ...session,
          name: session.session_name,
          project: { id: session.project_id, name: session.project_name },
          runDate,
          date: runDate ? runDate.toDateString() : null,
          monthKey: runDate ? `${runDate.getFullYear()}-${runDate.getMonth()}` : null,
          distanceFt,
          distanceMiles: (distanceFt / 5280).toFixed(2),
          avgIri: session.meta?.iri_avg_in_per_mi || null,
        };
      }).filter(s => s.date);

      setAllSessions(processed);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (suppliedSessions === undefined) loadSessions();
  }, [loadSessions, suppliedSessions]);

  // Generate months with session counts (12 months back, 6 months forward)
  const monthsWithCounts = useMemo(() => {
    const result = [];
    const today = new Date();
    
    // Count sessions per month
    const countMap = new Map();
    allSessions.forEach(session => {
      if (session.monthKey) {
        countMap.set(session.monthKey, (countMap.get(session.monthKey) || 0) + 1);
      }
    });
    
    const offsets = allSessions.filter(s => s.runDate).map(s => (s.runDate.getFullYear() - today.getFullYear()) * 12 + s.runDate.getMonth() - today.getMonth());
    const firstOffset = Math.min(-12, ...offsets);
    const lastOffset = Math.max(6, ...offsets);
    for (let i = firstOffset; i <= lastOffset; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      result.push({
        year: date.getFullYear(),
        month: date.getMonth(),
        key,
        label: date.toLocaleDateString('en-US', { month: 'short' }),
        fullLabel: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        count: countMap.get(key) || 0,
        isCurrentMonth: date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth(),
        isSelected: date.getFullYear() === currentDate.getFullYear() && date.getMonth() === currentDate.getMonth()
      });
    }
    return result;
  }, [allSessions, currentDate]);

  // Scroll current month into view
  useEffect(() => {
    if (monthScrollRef.current) {
      const selectedEl = monthScrollRef.current.querySelector('[data-selected="true"]');
      if (selectedEl) {
        selectedEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [currentDate]);

  // Project color map
  const projectColorMap = useMemo(() => {
    const uniqueProjects = [...new Set(allSessions.map(s => s.project?.id).filter(Boolean))];
    const map = new Map();
    uniqueProjects.forEach((projectId, index) => {
      map.set(projectId, PROJECT_COLORS[index % PROJECT_COLORS.length]);
    });
    return map;
  }, [allSessions]);

  // Group sessions by date
  const sessionsByDate = useMemo(() => {
    const grouped = new Map();
    allSessions.forEach(session => {
      if (!grouped.has(session.date)) {
        grouped.set(session.date, []);
      }
      grouped.get(session.date).push(session);
    });
    return grouped;
  }, [allSessions]);

  // Get calendar days for current month - always 6 rows (42 cells)
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    
    const days = [];
    
    // Previous month padding
    const prevMonth = new Date(year, month, 0);
    for (let i = startPadding - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    // Current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month padding - always fill to exactly 42 cells (6 rows)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  }, [currentDate]);

  // Navigation
  const goToPrevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
  };

  const isToday = (date) => date.toDateString() === new Date().toDateString();
  const isSelected = (date) => selectedDate === date.toDateString();

  const handleDayClick = (date, hasSessions) => {
    const dateKey = date.toDateString();
    if (hasSessions) {
      setSelectedDate(selectedDate === dateKey ? null : dateKey);
    } else {
      setSelectedDate(null);
    }
  };

  const selectedDateSessions = useMemo(() => {
    if (!selectedDate) return [];
    return sessionsByDate.get(selectedDate) || [];
  }, [selectedDate, sessionsByDate]);

  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const jumpToMonth = (year, month) => {
    setCurrentDate(new Date(year, month, 1));
    setSelectedDate(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
          <p className="text-gray-500 mt-3 text-sm">Loading calendar...</p>
        </div>
      </div>
    );
  }

  // Empty state - no sessions at all
  if (allSessions.length === 0 && !error) {
    return (
      <div className="h-screen flex flex-col bg-white">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md px-6">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Sessions Yet</h2>
            <p className="text-gray-500 mb-6">
              Upload your first road profile data to see sessions appear on the calendar.
            </p>
            <button
              onClick={() => navigate(`${routePrefix}/upload`)}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 transition-colors"
            >
              Go to Upload
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Hide scrollbar CSS */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      {/* Header */}
      <div className="flex-shrink-0 h-14 border-b border-gray-200 px-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">{monthYear}</h1>

        <div className="flex items-center gap-4">
          <p className="text-xs text-gray-400">Jump to month · numbers show sessions</p>
          <div className="inline-flex rounded border border-gray-300 divide-x divide-gray-300">
            <button
              onClick={goToPrevMonth}
              className="px-2 py-1 hover:bg-gray-100 transition-colors rounded-l"
            >
              <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Today
            </button>
            <button
              onClick={goToNextMonth}
              className="px-2 py-1 hover:bg-gray-100 transition-colors rounded-r"
            >
              <ChevronRightIcon className="h-4 w-4 text-gray-600" />
            </button>
          </div>
          {onViewChange && <ViewToggle view={view} onChange={onViewChange} />}
        </div>
      </div>

      {/* Month Navigation Bar */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50">
        <div 
          ref={monthScrollRef}
          className="flex overflow-x-auto py-2 px-4 gap-1 hide-scrollbar"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          {monthsWithCounts.map((m, index) => {
            // Show year label at start or when year changes
            const showYear = index === 0 || monthsWithCounts[index - 1]?.year !== m.year;
            
            return (
              <React.Fragment key={m.key}>
                {showYear && (
                  <div className="flex-shrink-0 flex items-center px-2 text-xs font-semibold text-gray-400">
                    {m.year}
                  </div>
                )}
                <button
                  data-selected={m.isSelected}
                  onClick={() => jumpToMonth(m.year, m.month)}
                  className={`
                    flex-shrink-0 flex flex-col items-center px-3 py-1.5 rounded transition-colors min-w-[56px]
                    ${m.isSelected 
                      ? 'bg-red-600 text-white' 
                      : m.isCurrentMonth 
                        ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                        : m.count > 0 
                          ? 'hover:bg-gray-200 text-gray-700'
                          : 'hover:bg-gray-200 text-gray-400'
                    }
                  `}
                >
                  <span className="text-xs font-medium">{m.label}</span>
                  {m.count > 0 ? (
                    <span className={`text-sm font-semibold ${m.isSelected ? 'text-white' : 'text-gray-900'}`}>
                      {m.count}
                    </span>
                  ) : (
                    <span className={`text-sm ${m.isSelected ? 'text-red-200' : 'text-gray-300'}`}>–</span>
                  )}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="flex-shrink-0 bg-red-50 border-b border-red-200 px-6 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Calendar */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Day Headers */}
        <div className="flex-shrink-0 grid grid-cols-7 border-b border-gray-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
            <div 
              key={day} 
              className={`py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wide
                ${i < 6 ? 'border-r border-gray-200' : ''}
              `}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid - fixed 6 rows */}
        <div className="flex-1 grid grid-cols-7 grid-rows-6">
          {calendarDays.map((dayInfo, index) => {
            const { date, isCurrentMonth } = dayInfo;
            const dateKey = date.toDateString();
            const daySessions = sessionsByDate.get(dateKey) || [];
            const hasSessions = daySessions.length > 0;
            const today = isToday(date);
            const selected = isSelected(date);
            const isLastCol = (index + 1) % 7 === 0;

            return (
              <div
                key={index}
                onClick={() => handleDayClick(date, hasSessions)}
                className={`
                  relative flex flex-col overflow-hidden border-b border-gray-200
                  ${!isLastCol ? 'border-r border-gray-200' : ''}
                  ${!isCurrentMonth ? 'bg-gray-50' : 'bg-white'}
                  ${hasSessions ? 'cursor-pointer hover:bg-gray-50' : ''}
                  ${selected ? 'ring-2 ring-inset ring-red-500 bg-red-50' : ''}
                `}
              >
                {/* Date Number */}
                <div className="flex justify-end p-1.5">
                  <span className={`
                    text-sm w-7 h-7 flex items-center justify-center rounded-full
                    ${today ? 'bg-red-600 text-white font-semibold' : ''}
                    ${!isCurrentMonth && !today ? 'text-gray-400' : ''}
                    ${isCurrentMonth && !today ? 'text-gray-900' : ''}
                  `}>
                    {date.getDate()}
                  </span>
                </div>

                {/* Session Events */}
                <div className="flex-1 px-1 pb-1 space-y-0.5 overflow-hidden">
                  {daySessions.slice(0, 3).map((session) => {
                    const colors = projectColorMap.get(session.project?.id) || PROJECT_COLORS[0];
                    return (
                      <div
                        key={session.session_id}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`${routePrefix}/session/${session.session_id}`);
                        }}
                        className={`
                          ${colors.bg} ${colors.text} 
                          text-xs px-1.5 py-0.5 rounded truncate cursor-pointer
                          hover:opacity-80 transition-opacity
                        `}
                        title={`${session.name || session.filename} - ${session.project?.name}`}
                      >
                        {session.name || session.filename?.replace('.ard', '') || 'Unnamed'}
                      </div>
                    );
                  })}
                  {daySessions.length > 3 && (
                    <div className="text-xs text-gray-500 px-1">
                      +{daySessions.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sidebar - appears when date selected */}
      {selectedDate && (
        <div className="fixed right-0 top-0 h-full w-80 bg-white border-l border-gray-200 shadow-lg z-50 flex flex-col">
          {/* Sidebar Header */}
          <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">
                  {new Date(selectedDate).toLocaleDateString('en-US', { 
                    weekday: 'long',
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {selectedDateSessions.length} session{selectedDateSessions.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => setSelectedDate(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Sessions List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {selectedDateSessions.map(session => {
              const colors = projectColorMap.get(session.project?.id) || PROJECT_COLORS[0];
              return (
                <div 
                  key={session.session_id}
                  onClick={() => navigate(`${routePrefix}/session/${session.session_id}`)}
                  className="bg-white border border-gray-200 rounded p-3 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-1 self-stretch rounded-full ${colors.bg}`} />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 text-sm truncate">
                        {session.name || session.filename?.replace('.ard', '') || 'Unnamed Session'}
                      </h4>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`${routePrefix}/projects/${session.project.id}`);
                        }}
                        className="text-xs text-gray-500 hover:text-red-600 truncate block"
                      >
                        {session.project.name}
                      </button>
                      
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <div className="flex items-center">
                          <MapPinIcon className="h-3.5 w-3.5 mr-1 text-gray-400" />
                          <span>{session.readings != null ? `${session.readings.toLocaleString()} readings` : `${session.distanceMiles} mi`}</span>
                        </div>
                        {session.avgIri && (
                          <div className="flex items-center">
                            <ChartBarIcon className="h-3.5 w-3.5 mr-1 text-gray-400" />
                            <span>{session.avgIri.toFixed(1)} IRI</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Overlay when sidebar is open */}
      {selectedDate && (
        <div 
          className="fixed inset-0 bg-black/10 z-40"
          onClick={() => setSelectedDate(null)}
        />
      )}
    </div>
  );
}